import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, X, Check } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import { EASE } from "@/components/primitives";

export default function ContactFloat() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSuccess = () => {
    setSent(true);
    window.setTimeout(() => {
      setOpen(false);
      window.setTimeout(() => setSent(false), 400);
    }, 2600);
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <motion.button
        onClick={() => setOpen(true)}
        data-testid="contact-float-button"
        aria-label="Contact Us — submit an enquiry"
        className="group fixed bottom-6 right-6 z-40 flex items-center gap-2.5 rounded-full bg-electric p-4 text-sm font-semibold text-ink shadow-[0_10px_30px_rgba(11,99,243,0.35)] transition-colors duration-300 hover:bg-teal sm:py-3.5 sm:pl-4 sm:pr-5"
        initial={{ opacity: 0, scale: 0.7, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.6, ease: EASE }}
        whileHover={{ y: -2 }}
      >
        <Mail className="h-5 w-5" />
        <span className="hidden sm:inline">Contact Us</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="contact-float-modal"
            className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <button
              aria-label="Close contact form"
              data-testid="contact-float-backdrop"
              onClick={() => setOpen(false)}
              className="absolute inset-0 bg-[#07101F]/60 backdrop-blur-sm"
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Contact Us"
              className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl"
              initial={{ opacity: 0, y: 48, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.98 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
              <button
                onClick={() => setOpen(false)}
                data-testid="contact-float-close"
                aria-label="Close"
                className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-line bg-panel text-mute transition-colors hover:border-electric/50 hover:text-electric"
              >
                <X className="h-4 w-4" />
              </button>
              {sent ? (
                <div data-testid="contact-float-success" className="flex flex-col items-center rounded-3xl border border-line bg-panel px-8 py-20 text-center">
                  <motion.span
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-teal/10 text-teal"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, ease: EASE }}
                  >
                    <Check className="h-8 w-8" />
                  </motion.span>
                  <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-mist">Enquiry Submitted</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-mute">
                    Thank you — your message is with the founding team. We typically respond within 2–3 business days.
                  </p>
                </div>
              ) : (
                <EnquiryForm
                  defaultInterest="General Enquiry"
                  source="floating-contact"
                  heading="Contact Us"
                  lead="One tap, straight to the founding team — we typically respond within 2–3 business days."
                  testid="float-contact"
                  onSuccess={handleSuccess}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
